import React, { FC, useCallback } from 'react'
import styles from 'styles/components/template.module.scss'
import { Spacer } from 'components/Utility'
import { GreyButton, OrangeButton } from 'components/Button'
import { MainHeadline } from 'components/Typography'
import { useDispatch, useSelector } from 'react-redux'
import { push } from 'connected-react-router'
import { AlertCard, CardGreyHeadline, CardOutlined, CardOutlinedInner, CouponCard } from 'components/Card'
import { FormLabel } from 'components/Label'
import { CouponType } from 'types/coupon'
import { State } from 'index'
import { getConfirmCoupon } from 'reducks/coupon/selectors'
import { useQueryAllStores } from 'hooks/store'
import format from 'date-fns/format'
import HTMLReactParser from 'html-react-parser'
import { isFilledCouponRequiredFields } from 'lib/judge'
import { registerCoupon } from 'reducks/coupon/operations'
import startOfToday from 'date-fns/startOfToday'
import { useQueryCouponCodes } from 'hooks/coupon'
import { parseSecondsToDays } from 'lib/helper'

const CouponConfirmTemplate: FC = () => {
  const dispatch = useDispatch()
  const backToCouponCreatePage = useCallback(() => dispatch(push('/admin/coupon/create')), [dispatch])
  const coupon: CouponType = useSelector((state: State) => getConfirmCoupon(state))
  const stores = useQueryAllStores()
  const couponCodes = useQueryCouponCodes()
  const isFilledRequiredFields = isFilledCouponRequiredFields(
    coupon.name,
    coupon.code,
    couponCodes,
    coupon.description,
    coupon.discount_amount,
    coupon.discount_rate,
    coupon.limitations.max_discount || 0,
    coupon.limitations.min_amount || 0,
    (coupon.targets && coupon.targets?.stores) || undefined
  )

  const register = () => dispatch(registerCoupon(coupon, couponCodes))

  return (
    <div className={styles.standard}>
      <Spacer size={28} />
      <MainHeadline headline={'クーポン登録確認'} />
      <Spacer size={20} />
      <AlertCard
        headline={'一度登録したクーポンは、編集することはできないため注意して登録してください。'}
        title={'注意'}
      />
      <Spacer size={20} />
      <FormLabel label={'プレビュー'} required={false} />
      <Spacer size={8} />
      <CouponCard
        createdDate={startOfToday()}
        description={coupon.description}
        name={coupon.name}
        discountAmount={coupon.discount_amount}
        discountRate={coupon.discount_rate}
        maxDiscount={coupon.limitations.max_discount || 0}
        maxPerUser={coupon.limitations.max_per_user || 0}
        minAmount={coupon.limitations.min_amount || 0}
        expiresAt={coupon.expires_at || undefined}
        expiresMaxage={coupon.expires_maxage || 0}
        isFirst={coupon.limitations.is_first}
        storeIds={coupon.targets && coupon.targets.stores ? coupon.targets.stores : []}
        stores={stores}
      />
      <Spacer size={20} />
      <FormLabel label={'登録内容'} required={false} />
      <Spacer size={24} />
      <CardOutlined>
        <CardGreyHeadline headline={'クーポン名'} isTop={true} />
        <Spacer size={8} />
        <CardOutlinedInner>{coupon.name !== '' ? '有り' : '無し'}</CardOutlinedInner>
        <Spacer size={8} />
        <CardGreyHeadline headline={'クーポンコード'} />
        <Spacer size={8} />
        <CardOutlinedInner>{coupon.code !== '' ? '有り' : '無し（自動発行）'}</CardOutlinedInner>
        <Spacer size={8} />
        <CardGreyHeadline headline={'クーポン説明'} />
        <Spacer size={8} />
        <CardOutlinedInner>{coupon.description !== '' ? '有り' : '無し'}</CardOutlinedInner>
        <Spacer size={8} />
        <CardGreyHeadline headline={'割引方法'} />
        <Spacer size={8} />
        <CardOutlinedInner>
          {coupon.discount_amount > 0 || coupon.discount_rate > 0 ? '有り' : '無し'}
        </CardOutlinedInner>
        <Spacer size={8} />
        <CardGreyHeadline headline={`割引${coupon.discount_amount > 0 ? '金額' : '率'}`} />
        <Spacer size={8} />
        <CardOutlinedInner>
          {coupon.discount_amount > 0 || coupon.discount_rate > 0 ? '有り' : '無し'}
        </CardOutlinedInner>
        <Spacer size={8} />
        <CardGreyHeadline headline={'割引額上限'} />
        <Spacer size={8} />
        <CardOutlinedInner>{coupon.limitations.max_discount ? '有り' : '無し'}</CardOutlinedInner>
        <Spacer size={8} />
        <CardGreyHeadline headline={'最小決済金額'} />
        <Spacer size={8} />
        <CardOutlinedInner>{coupon.limitations.min_amount ? '有り' : '無し'}</CardOutlinedInner>
        <Spacer size={8} />
        <CardGreyHeadline headline={'クーポン有効期限'} />
        <Spacer size={8} />
        <CardOutlinedInner>
          固定期限 : {coupon.expires_at ? format(coupon.expires_at.toDate(), 'yyyy年MM月dd日') : '無し'}
          <br />
          相対期限 : {coupon.expires_maxage ? parseSecondsToDays(coupon.expires_maxage) + '日後' : '無し'}
        </CardOutlinedInner>
        <Spacer size={8} />
        <CardGreyHeadline headline={'クーポン配布上限'} />
        <Spacer size={8} />
        <CardOutlinedInner>
          配布上限数: {coupon.limitations.max_total ? coupon.limitations.max_total.toLocaleString() : '無し'}
          <br />
          1ユーザーあたりの取得上限 :{' '}
          {coupon.limitations.max_per_user ? coupon.limitations.max_per_user.toLocaleString() : '無し'}
        </CardOutlinedInner>
        <Spacer size={8} />
        <CardGreyHeadline headline={'取得条件'} />
        <Spacer size={8} />
        <CardOutlinedInner>
          {coupon.limitations.is_first ? HTMLReactParser(`・初回購入限定<br/>`) : ''}
          {coupon.type === 'manual' ? `・限定したユーザーへ配布` : ''}
          {!coupon.limitations.is_first && coupon.type !== 'manual' ? '無し' : ''}
        </CardOutlinedInner>
        <Spacer size={8} />
        <CardGreyHeadline headline={'利用上限'} />
        <Spacer size={8} />
        <CardOutlinedInner>
          {coupon.targets && coupon.targets.stores && coupon.targets.stores.length > 0
            ? HTMLReactParser(`・店舗制限<br/>`)
            : '無し'}
          {coupon.targets &&
            coupon.targets.stores &&
            coupon.targets.stores
              .map((storeId) => {
                const store = stores.find((store) => store.store_id && store.store_id === storeId)
                if (!store) return ''
                return store.name
              })
              .join(`、`)}
        </CardOutlinedInner>
        <Spacer size={8} />
      </CardOutlined>
      <Spacer size={20} />
      <OrangeButton
        disabled={!isFilledRequiredFields}
        label={'クーポンを登録する'}
        onClick={register}
        size={'medium'}
        type={'main'}
      />
      <Spacer size={20} />
      <GreyButton
        label={'クーポン新規登録画面へ戻る'}
        onClick={backToCouponCreatePage}
        size={'medium'}
        type={'outlined'}
      />
      <Spacer size={72} />
    </div>
  )
}

export default CouponConfirmTemplate
